import { TEAM_MEMBERS, MAX_HOLIDAY_DAYS } from './config';
import { formatDate, calculateDuration, getDaysInRange, calculateOverlaps } from './utils';
import type { Holiday } from './types';

// UI Elements
const holidayListDiv = document.getElementById('holiday-list') as HTMLDivElement;
const logButton = document.getElementById('log-button') as HTMLButtonElement;
const errorMsg = document.getElementById('error-message') as HTMLParagraphElement;
const userIdDisplay = document.getElementById('user-id-display') as HTMLSpanElement;
const holidayForm = document.getElementById('holiday-form') as HTMLFormElement;
const startDateInput = document.getElementById('start-date') as HTMLInputElement;
const endDateInput = document.getElementById('end-date') as HTMLInputElement;
const nameSelect = document.getElementById('employee-name') as HTMLSelectElement;
const usageNameSelect = document.getElementById('usage-name-select') as HTMLSelectElement;
const usageOutput = document.getElementById('usage-output') as HTMLDivElement;

let employeeUsageData = new Map<string, number>(); // Global map to store {name: total_days}

/**
 * Updates the employee usage data map.
 */
export function updateEmployeeUsageData(usageData: Map<string, number>): void {
  employeeUsageData = usageData;
  displayUsageForSelectedEmployee();
}

/**
 * Gets the usage data for a specific employee.
 */
export function getEmployeeUsage(name: string): number {
  return employeeUsageData.get(name) || 0;
}

/**
 * Populates the employee name dropdowns with the TEAM_MEMBERS list.
 */
export function populateEmployeeSelect(): void {
  TEAM_MEMBERS.forEach(name => {
    // Populate main logging dropdown
    const option1 = document.createElement('option');
    option1.value = name;
    option1.textContent = name;
    nameSelect.appendChild(option1);

    // Populate usage summary dropdown
    const option2 = document.createElement('option');
    option2.value = name;
    option2.textContent = name;
    usageNameSelect.appendChild(option2);
  });
  // Ensure the select defaults to the first option if no blank option exists
  if (nameSelect.value === "") {
    nameSelect.selectedIndex = 0;
  }
}

/**
 * Updates the display area with the total days used by the selected employee,
 * showing remaining days and highlighting overages/low balances.
 */
export function displayUsageForSelectedEmployee(): void {
  const selectedName = usageNameSelect.value;
  const allowance = MAX_HOLIDAY_DAYS;

  if (!selectedName || selectedName === "Select Employee") {
    usageOutput.innerHTML = `<p class="text-gray-500">Select an employee to view their total days used.</p>`;
    return;
  }

  const totalDaysUsed = employeeUsageData.get(selectedName) || 0;
  const daysRemaining = allowance - totalDaysUsed;

  // Determine styling based on remaining days
  let remainingClasses = 'text-green-600';
  let remainingMessage = '';

  if (daysRemaining < 0) {
    remainingClasses = 'text-red-600';
    remainingMessage = `(OVER LIMIT by ${Math.abs(daysRemaining)} days!)`;
  } else if (daysRemaining <= 5) {
    remainingClasses = 'text-yellow-600';
    remainingMessage = `(Low balance: ${daysRemaining} days left)`;
  }

  const usedText = totalDaysUsed === 1 ? 'day' : 'days';

  usageOutput.innerHTML = `
    <div class="flex flex-col sm:flex-row justify-between items-center w-full">
      <p class="text-xl font-semibold text-gray-700 mb-1 sm:mb-0">
        Used: <span class="text-2xl font-extrabold text-indigo-600">${totalDaysUsed}</span> ${usedText}
      </p>
      <p class="text-xl font-semibold text-gray-700">
        Remaining: <span class="text-2xl font-extrabold ${remainingClasses}">${daysRemaining}</span> days
      </p>
    </div>
    ${remainingMessage ? `<p class="text-sm font-bold mt-2 ${remainingClasses}">${remainingMessage}</p>` : ''}
    <p class="text-xs text-gray-500 mt-2">Annual allowance: ${allowance} days</p>
  `;
}

/**
 * Sets up the listener for the usage dropdown.
 */
export function setupUsageListener(): void {
  // Attach listener to the usage dropdown
  usageNameSelect.addEventListener('change', displayUsageForSelectedEmployee);
  // Set initial state
  displayUsageForSelectedEmployee();
}

/**
 * Renders the list of holidays on the page, highlights overlaps, and updates usage totals.
 */
export function renderHolidays(holidays: Holiday[], onDelete: (docId: string) => void): void {
  holidayListDiv.innerHTML = '';

  if (holidays.length === 0) {
    holidayListDiv.innerHTML = '<p class="text-center text-gray-500 p-4">No team holidays logged yet. Add one above!</p>';
    // Reset usage data and display if no holidays exist
    updateEmployeeUsageData(new Map());
    return;
  }

  // --- 1. Calculate Overlaps ---
  const dateCountMap = calculateOverlaps(holidays);

  // Create a Set of all dates that have 2 or more people absent
  const overlappingDates = new Set<string>();
  dateCountMap.forEach((count, dateKey) => {
    if (count >= 2) {
      overlappingDates.add(dateKey);
    }
  });
  // --- End Calculate Overlaps ---

  // --- 2. Calculate Employee Usage ---
  const newUsageData = new Map<string, number>();
  holidays.forEach(holiday => {
    const name = holiday.name;
    // Get duration in days (needs the number, not the formatted text)
    const durationInDays = calculateDuration(holiday.startDate, holiday.endDate).durationDays;

    const currentTotal = newUsageData.get(name) || 0;
    newUsageData.set(name, currentTotal + durationInDays);
  });
  // Update global map and summary display
  updateEmployeeUsageData(newUsageData);
  // --- End Calculate Employee Usage ---

  // Sort locally by start date (ascending)
  holidays.sort((a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime());

  holidays.forEach(holiday => {
    // --- 3. Check if the current holiday clashes with any overlap date ---
    let isOverlappingHoliday = false;
    const holidayDays = getDaysInRange(holiday.startDate, holiday.endDate);

    // Check if any date in this holiday's range is in the overlappingDates Set
    for (const day of holidayDays) {
      if (overlappingDates.has(day)) {
        isOverlappingHoliday = true;
        break;
      }
    }

    // --- 4. Apply dynamic classes based on overlap status ---
    const cardClasses = isOverlappingHoliday
      ? 'bg-red-50 p-4 rounded-lg shadow-md border-2 border-red-500 flex flex-col sm:flex-row justify-between items-start sm:items-center transition duration-300'
      : 'bg-white p-4 rounded-lg shadow-md border border-indigo-100 flex flex-col sm:flex-row justify-between items-start sm:items-center';

    const nameClasses = isOverlappingHoliday ? 'text-red-700' : 'text-gray-800';
    const dateClasses = isOverlappingHoliday ? 'text-red-600' : 'text-indigo-600';
    const durationClasses = isOverlappingHoliday
      ? 'bg-red-200 text-red-800 px-3 py-1 rounded-full font-medium'
      : 'bg-indigo-100 text-indigo-800 px-3 py-1 rounded-full font-medium';

    const itemDiv = document.createElement('div');
    itemDiv.className = cardClasses;

    const startDate = formatDate(holiday.startDate);
    const endDate = formatDate(holiday.endDate);
    const name = holiday.name || 'Unknown Employee';

    // Recalculate duration for display using the helper function for consistency
    const duration = calculateDuration(holiday.startDate, holiday.endDate);
    const durationText = duration.durationText;

    itemDiv.innerHTML = `
      <div class="mb-2 sm:mb-0">
        <p class="text-xl font-bold ${nameClasses}">
          ${isOverlappingHoliday ? '🚨 OVERLAP: ' : ''}${name}
        </p>
        <p class="text-lg font-semibold ${dateClasses} mt-1">
          ${startDate} <span class="text-gray-400 font-normal mx-1">&mdash;</span> ${endDate}
        </p>
      </div>
      <div class="flex items-center space-x-3 text-sm text-gray-500">
        <span class="${durationClasses}">
          ${durationText}
        </span>
        <!-- Delete Button -->
        <button data-id="${holiday.id}" class="delete-btn text-red-500 hover:text-red-700 p-1 rounded-full hover:bg-red-100 transition duration-150" title="Delete Holiday">
          <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
            <path fill-rule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm4 0a1 1 0 10-2 0v6a1 1 0 102 0V8z" clip-rule="evenodd" />
          </svg>
        </button>
      </div>
    `;
    holidayListDiv.appendChild(itemDiv);

    // Attach listener to the delete button
    const deleteButton = itemDiv.querySelector('.delete-btn') as HTMLButtonElement;
    if (deleteButton) {
      deleteButton.addEventListener('click', () => {
        const docId = deleteButton.getAttribute('data-id');
        if (docId) {
          onDelete(docId);
        }
      });
    }
  });
}

/**
 * Updates the user ID display.
 */
export function updateUserIdDisplay(userId: string): void {
  userIdDisplay.textContent = userId;
}

/**
 * Shows an error message.
 */
export function showError(message: string): void {
  errorMsg.textContent = message;
  errorMsg.classList.remove('hidden');
}

/**
 * Hides the error message.
 */
export function hideError(): void {
  errorMsg.classList.add('hidden');
}

/**
 * Sets the log button state.
 */
export function setLogButtonState(disabled: boolean, text: string = 'Log Holiday Dates'): void {
  logButton.disabled = disabled;
  logButton.textContent = text;
}

/**
 * Clears the date inputs.
 */
export function clearDateInputs(): void {
  startDateInput.value = '';
  endDateInput.value = '';
}

/**
 * Gets form values.
 */
export function getFormValues(): { name: string; startDate: string; endDate: string } | null {
  const employeeName = nameSelect.value;
  const startDate = startDateInput.value;
  const endDate = endDateInput.value;

  if (!employeeName || employeeName === "Select Team Member") {
    return null;
  }

  return { name: employeeName, startDate, endDate };
}

/**
 * Sets up the form submit handler.
 */
export function setupFormHandler(onSubmit: (event: Event) => void): void {
  holidayForm.addEventListener('submit', onSubmit);
}

/**
 * Sets the loading indicator.
 */
export function setLoadingIndicator(loading: boolean): void {
  const loadingIndicator = document.getElementById('loading-indicator');
  if (loadingIndicator) {
    loadingIndicator.textContent = loading ? 'Loading holidays...' : '';
  }
}

/**
 * Sets the holiday list error message.
 */
export function setHolidayListError(message: string): void {
  holidayListDiv.innerHTML = `<p class="text-center text-red-500 p-4">${message}</p>`;
}

